import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Cloud,
    Monitor,
    Server,
    Cpu,
    Network,
    Headphones,
    Settings,
    ArrowRight,
    CheckCircle2,
    Zap,
    Lock,
    Database,
    Globe,
    HardDrive,
    Code,
    Cog,
    Briefcase,
    Stethoscope,
    Package,
    Laptop,
    Bot
} from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import ModernCTA from '../../components/CTA/ModernCTA';
import './ModernServicePage.scss';

const ModernServicePage = () => {
    const services = [
        {
            icon: <Bot size={32} />,
            title: 'AI Automation & Workflows',
            description: 'Spending too much time on repetitive tasks? We create internal and external automations, build AI copilots into your apps, and set up AI social media content workflows in Make and n8n, then deploy them on AWS.',
            features: ['Internal & External Automations', 'In-App AI Copilots', 'AI Social Media Content', 'Make & n8n on AWS'],
            link: '/ai-automation',
            color: 'indigo',
            featured: true
        },
        {
            icon: <Server size={32} />,
            title: 'Hardware Solutions',
            description: 'Enterprise-grade hardware from leading manufacturers at competitive prices. Servers, workstations, networking equipment and more.',
            features: ['Servers & Storage', 'Workstations', 'Networking', 'Peripherals'],
            link: '/accessories',
            color: 'purple'
        },
        {
            icon: <Cpu size={32} />,
            title: 'Software Licensing',
            description: 'Authorized software licensing and subscription management services. Get the right software at the best prices.',
            features: ['Volume Licensing', 'Subscription Management', 'Compliance', 'Renewals'],
            link: '/contact',
            color: 'orange'
        },
        {
            icon: <Monitor size={32} />,
            title: 'IT Modernization',
            description: 'Transform legacy systems with cutting-edge technology. We help modernize your infrastructure for improved performance.',
            features: ['Legacy Migration', 'System Integration', 'Digital Transformation', 'Automation'],
            link: '/it-modernization',
            color: 'cyan'
        },
        {
            icon: <Headphones size={32} />,
            title: 'Managed IT Services',
            description: '24/7 technical support and managed services to keep your operations running smoothly. Proactive monitoring and maintenance.',
            features: ['24/7 Support', 'Proactive Monitoring', 'Help Desk', 'Maintenance'],
            link: '/contact',
            color: 'red'
        },
        {
            icon: <Network size={32} />,
            title: 'Network Infrastructure',
            description: 'Design, implement, and maintain robust network solutions. From planning to deployment and ongoing support.',
            features: ['Network Design', 'Implementation', 'Monitoring', 'Optimization'],
            link: '/contact',
            color: 'green'
        },
        {
            icon: <Cloud size={32} />,
            title: 'Cloud Solutions',
            description: 'Scalable cloud infrastructure and migration services. We help you leverage the power of cloud computing for enhanced efficiency.',
            features: ['Cloud Migration', 'AWS/Azure/GCP', 'Hybrid Solutions', 'Cost Optimization'],
            link: '/digital',
            color: 'blue'
        },
        {
            icon: <Settings size={32} />,
            title: 'Professional Services',
            description: 'Expert consulting and implementation services. Our certified professionals help you plan and execute technology projects.',
            features: ['Consulting', 'Implementation', 'Training', 'Project Management'],
            link: '/contact',
            color: 'indigo'
        },
        {
            icon: <Shield size={32} />,
            title: 'Cybersecurity Solutions',
            description: 'Comprehensive security solutions to protect your organization from evolving cyber threats. From endpoint protection to advanced threat detection.',
            features: ['Threat Detection', 'Endpoint Protection', 'Security Audits', 'Compliance'],
            link: '/cybersecurity',
            color: 'primary'
        }
    ];

    const benefits = [
        {
            icon: <Zap size={24} />,
            title: 'Fast Deployment',
            description: 'Quick procurement and deployment to minimize downtime'
        },
        {
            icon: <Lock size={24} />,
            title: 'Government Compliant',
            description: 'All solutions meet stringent government standards'
        },
        {
            icon: <Database size={24} />,
            title: 'Scalable Solutions',
            description: 'Flexible solutions that grow with your organization'
        },
        {
            icon: <Globe size={24} />,
            title: 'Nationwide Support',
            description: 'Support services available across the country'
        }
    ];

    const offerings = [
        {
            icon: <HardDrive size={32} />,
            title: 'Hardware Products & Services',
            description: 'Top-tier hardware products and dedicated services for peak performance. Our experts help with selection, installation, and maintenance.',
            features: ['Servers & Storage', 'Workstations', 'Networking Equipment', 'Peripherals'],
            link: '/accessories',
            color: 'primary'
        },
        {
            icon: <Code size={32} />,
            title: 'Software Products & Services',
            description: 'Wide array of software solutions to streamline operations and improve productivity. End-to-end support from selection to troubleshooting.',
            features: ['Volume Licensing', 'Cloud Software', 'Security Solutions', 'Custom Development'],
            link: '/contact',
            color: 'cyan'
        },
        {
            icon: <Network size={32} />,
            title: 'Telecommunications & Network',
            description: 'Robust, reliable, and scalable network services. Optimize your communication infrastructure for seamless collaboration.',
            features: ['Network Design', 'VoIP Solutions', 'Fiber Connectivity', 'Wireless Systems'],
            link: '/contact',
            color: 'indigo'
        },
        {
            icon: <Cog size={32} />,
            title: 'Industrial Products',
            description: 'Comprehensive industrial solutions. Install, maintain, repair, and build equipment that enhances operational efficiency and safety.',
            features: ['Safety Equipment', 'Tools & Machinery', 'MRO Supplies', 'Facility Equipment'],
            link: '/contact',
            color: 'slate'
        },
        {
            icon: <Briefcase size={32} />,
            title: 'Office Equipment & Supplies',
            description: 'One-stop destination for high-quality office equipment. From stationery to advanced machinery for productive work environments.',
            features: ['Furniture', 'Printing Solutions', 'Office Supplies', 'Breakroom Essentials'],
            link: '/contact',
            color: 'green'
        },
        {
            icon: <Stethoscope size={32} />,
            title: 'Medical Equipment',
            description: 'Essential medical equipment for healthcare facilities. Strict quality standards ensure reliability and effectiveness for patient care.',
            features: ['Diagnostic Equipment', 'Patient Monitors', 'Lab Instruments', 'Medical Supplies'],
            link: '/health-care',
            color: 'red'
        }
    ];

    const managedServices = [
        {
            icon: <Server size={28} />,
            title: 'Infrastructure Management',
            description: 'Complete management of your IT infrastructure including servers, storage, and networks.'
        },
        {
            icon: <Shield size={28} />,
            title: 'Security Operations',
            description: '24/7 security monitoring, threat detection, and incident response services.'
        },
        {
            icon: <Laptop size={28} />,
            title: 'End-User Support',
            description: 'Comprehensive help desk and desktop support for your entire organization.'
        },
        {
            icon: <Headphones size={28} />,
            title: 'NOC Services',
            description: 'Network Operations Center providing round-the-clock monitoring and support.'
        }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Our Services"
                subtitle="Comprehensive IT solutions tailored to meet your organization's unique needs"
                breadcrumbs={[{ label: 'Services' }]}
            />

            {/* Services Grid */}
            <section className="jk-services-page">
                <div className="jk-services-page__container">
                    <div className="jk-services-page__header">
                        <span className="jk-section-label">
                            <Settings size={16} />
                            What We Offer
                        </span>
                        <h2 className="jk-services-page__title">
                            Technology Solutions for
                            <span className="highlight"> Every Need</span>
                        </h2>
                        <p className="jk-services-page__subtitle">
                            From AI automation to hardware procurement and complete digital transformation,
                            we provide end-to-end IT solutions for government and commercial organizations.
                        </p>
                    </div>

                    <div className="jk-services-page__grid">
                        {services.map((service, index) => (
                            <div key={index} className={`jk-services-page__card jk-services-page__card--${service.color}${service.featured ? ' jk-services-page__card--featured' : ''}`}>
                                <div className="jk-services-page__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-services-page__card-title">{service.title}</h3>
                                <p className="jk-services-page__card-description">{service.description}</p>

                                <ul className="jk-services-page__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={16} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link to={service.link} className="jk-services-page__card-link">
                                    Learn More
                                    <ArrowRight size={18} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Managed Services Section */}
            <section className="jk-managed-services">
                <div className="jk-managed-services__container">
                    <div className="jk-managed-services__grid">
                        <div className="jk-managed-services__content">
                            <span className="jk-section-label">
                                <Headphones size={16} />
                                Managed Services
                            </span>
                            <h2 className="jk-managed-services__title">
                                Let Us Handle Your
                                <span className="highlight"> IT Operations</span>
                            </h2>
                            <p className="jk-managed-services__text">
                                Focus on your core business while we take care of your technology infrastructure.
                                Our managed services provide proactive monitoring, maintenance, and support to
                                keep your systems running at peak performance.
                            </p>

                            <div className="jk-managed-services__features">
                                {managedServices.map((service, index) => (
                                    <div key={index} className="jk-managed-services__feature">
                                        <div className="jk-managed-services__feature-icon">
                                            {service.icon}
                                        </div>
                                        <div className="jk-managed-services__feature-content">
                                            <h4 className="jk-managed-services__feature-title">{service.title}</h4>
                                            <p className="jk-managed-services__feature-text">{service.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Explore Managed Services
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-managed-services__visual">
                            <div className="jk-managed-services__stat-grid">
                                <div className="jk-managed-services__stat">
                                    <span className="jk-managed-services__stat-number">99.9%</span>
                                    <span className="jk-managed-services__stat-label">Uptime SLA</span>
                                </div>
                                <div className="jk-managed-services__stat">
                                    <span className="jk-managed-services__stat-number">24/7</span>
                                    <span className="jk-managed-services__stat-label">Monitoring</span>
                                </div>
                                <div className="jk-managed-services__stat">
                                    <span className="jk-managed-services__stat-number">&lt;15min</span>
                                    <span className="jk-managed-services__stat-label">Response Time</span>
                                </div>
                                <div className="jk-managed-services__stat">
                                    <span className="jk-managed-services__stat-number">500+</span>
                                    <span className="jk-managed-services__stat-label">Clients Served</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Solution Categories Section */}
            <section className="jk-offerings">
                <div className="jk-offerings__container">
                    <div className="jk-offerings__header">
                        <span className="jk-section-label">
                            <Package size={16} />
                            Our Solutions
                        </span>
                        <h2 className="jk-offerings__title">
                            Comprehensive Technology
                            <span className="highlight"> Solutions</span>
                        </h2>
                        <p className="jk-offerings__subtitle">
                            From hardware to software, telecommunications to medical equipment - we provide
                            integrated solutions for all your technology needs.
                        </p>
                    </div>

                    <div className="jk-offerings__grid">
                        {offerings.map((offering, index) => (
                            <div key={index} className={`jk-offerings__card jk-offerings__card--${offering.color}`}>
                                <div className="jk-offerings__card-icon">
                                    {offering.icon}
                                </div>
                                <h3 className="jk-offerings__card-title">{offering.title}</h3>
                                <p className="jk-offerings__card-description">{offering.description}</p>

                                <ul className="jk-offerings__card-features">
                                    {offering.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link to={offering.link} className="jk-offerings__card-link">
                                    Learn More
                                    <ArrowRight size={18} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="jk-services-benefits">
                <div className="jk-services-benefits__container">
                    <div className="jk-services-benefits__header">
                        <h2 className="jk-services-benefits__title">
                            Why Choose Our Services?
                        </h2>
                    </div>

                    <div className="jk-services-benefits__grid">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="jk-services-benefits__card">
                                <div className="jk-services-benefits__card-icon">
                                    {benefit.icon}
                                </div>
                                <h3 className="jk-services-benefits__card-title">{benefit.title}</h3>
                                <p className="jk-services-benefits__card-text">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="jk-services-process">
                <div className="jk-services-process__container">
                    <div className="jk-services-process__header">
                        <span className="jk-section-label">
                            <Zap size={16} />
                            Our Process
                        </span>
                        <h2 className="jk-services-process__title">
                            How We <span className="highlight">Work</span>
                        </h2>
                    </div>

                    <div className="jk-services-process__steps">
                        <div className="jk-services-process__step">
                            <div className="jk-services-process__step-number">01</div>
                            <h3 className="jk-services-process__step-title">Discovery</h3>
                            <p className="jk-services-process__step-text">
                                We analyze your requirements and understand your unique challenges.
                            </p>
                        </div>
                        <div className="jk-services-process__step">
                            <div className="jk-services-process__step-number">02</div>
                            <h3 className="jk-services-process__step-title">Planning</h3>
                            <p className="jk-services-process__step-text">
                                Our experts design a customized solution tailored to your needs.
                            </p>
                        </div>
                        <div className="jk-services-process__step">
                            <div className="jk-services-process__step-number">03</div>
                            <h3 className="jk-services-process__step-title">Implementation</h3>
                            <p className="jk-services-process__step-text">
                                We deploy solutions with minimal disruption to your operations.
                            </p>
                        </div>
                        <div className="jk-services-process__step">
                            <div className="jk-services-process__step-number">04</div>
                            <h3 className="jk-services-process__step-title">Support</h3>
                            <p className="jk-services-process__step-text">
                                Ongoing support and optimization to ensure continued success.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernServicePage;
